import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {AsyncPipe, NgIf, NgTemplateOutlet, TitleCasePipe} from "@angular/common";
import {CustomerService} from "../../services/customer.service";
import {StoreService} from "../../services/store.service";
import {StorageService} from "../../services/storage.service";
import {Subscription, take} from "rxjs";
import {inputErrorAnimation} from "../../animation/animation";
import {CheckinStatus, Customer, CustomerCheckin} from "../../model/customer";
import {ToastService} from "../common/toast/toast.service";
import {ToastType} from "../common/toast/toast.component";
import {MatDialog} from "@angular/material/dialog";
import {ButtonComponent} from "../common/button/button.component";
import {Router} from "@angular/router";
import {UsPhoneNumberPipe} from "../../pipes/us-phone-number.pipe";

@Component({
  selector: 'app-check-in',
  imports: [
    FormsModule,
    UsPhoneNumberPipe,
    ReactiveFormsModule,
    NgTemplateOutlet,
    ButtonComponent,
    ButtonComponent
  ],
  templateUrl: './check-in.component.html',
  styleUrl: './check-in.component.scss',
  animations: [
    inputErrorAnimation
  ]
})
export class CheckInComponent implements OnInit, OnDestroy {
  storeId: string = '';
  customer: Customer | null = null;
  form: FormGroup = new FormGroup({
    phoneNumber: new FormControl<string>('', [Validators.required]),
    firstName: new FormControl<string>('', [Validators.required]),
    lastName: new FormControl<string>('', [Validators.required]),
  })
  storeName: string = 'Venus';
  customerFound: boolean = false;
  isSearching: boolean = false;
  isCheckin: boolean = false;
  searchFailed: boolean = false;

  currentStep: number = 1;
  smsPreference: 'all' | 'none' | null = 'all';

  subStore?: Subscription
  subSearch?: Subscription
  subCheckin?: Subscription

  constructor(
    private customerService: CustomerService,
    private storeService: StoreService,
    private storageService: StorageService,
    private toastService: ToastService,
    private router: Router,
    private dialog: MatDialog,
  ) {
  }

  ngOnInit(): void {
    this.storeId = this.storageService.getUser()?.storeId ?? ''

    this.subStore = this.storeService.getStoreById(this.storeId).subscribe(store => {
      if(!store) return;
      this.storeName = store.name;
    })
  }

  ngOnDestroy(): void {
    this.subStore?.unsubscribe();
    this.subSearch?.unsubscribe();
    this.subCheckin?.unsubscribe();
  }

  appendNumber(num: string): void {
    let originalPhoneNumber = this.getOriginalPhoneNumber(this.form.value.phoneNumber);
    if (this.form.value.phoneNumber !== null && originalPhoneNumber.length >= 10) {
      return;
    }
    this.form.patchValue({
      phoneNumber: this.form.value.phoneNumber + num
    })
    if (this.isAbleToSearch()) {
      this.searchCustomer();
    }
  }

  deleteNumber(): void {
    this.form.patchValue({
      phoneNumber: this.form.value.phoneNumber.slice(0, -1)
    })
    this.searchFailed = false;
  }

  searchCustomer(): void {
    if (!this.isAbleToSearch() || this.isSearching) return;
    let phoneNumber = this.getOriginalPhoneNumber(this.form.value.phoneNumber)
    this.isSearching = true;
    this.searchFailed = false;
    this.subSearch?.unsubscribe()
    this.subSearch = this.customerService.getCustomerByPhoneNumber(this.storeId, phoneNumber).subscribe({
      next: (searchResp) => {
        this.isSearching = false;
        if (!searchResp) return;
        if(searchResp.customer) {
          this.customer = searchResp.customer;
          this.form.patchValue({
            firstName: this.customer.firstName,
            lastName: this.customer.lastName
          })
        }
        this.customerFound = true;
        this.currentStep = 2;
      },
      error: () => {
        this.isSearching = false;
        this.searchFailed = true;
        this.toastService.show("Unable to search customer. Please try again", ToastType.ERROR)
      }
    })
  }

  handleBackToPhoneInput() {
    this.customerFound = false;
    this.currentStep = 1;
    this.searchFailed = false;
    this.form.patchValue({
      firstName: '',
      lastName: ''
    })
  }

  handleContinueToNotification(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.isCheckin) return;

    this.isCheckin = true;
    this.subCheckin?.unsubscribe()
    this.subCheckin = this.customerService.customerCheckin(this.getCustomer()).subscribe({
      next: (data: Customer | null) => {
        this.isCheckin = false;
        this.currentStep = 3;
        this.smsPreference = 'all';
      },
      error: () => {
        this.isCheckin = false;
        this.toastService.show("There was an error checking in. Please try again", ToastType.ERROR)
      }
    })
  }

  handleBackToInfo(): void {
    this.currentStep = 2;
  }

  selectSmsPreference(preference: 'all' | 'none'): void {
    if (this.smsPreference === preference) {
      this.smsPreference = null;
    } else {
      this.smsPreference = preference;
    }
  }

  isAbleToSearch(): boolean {
    let phoneNumber = this.getOriginalPhoneNumber(this.form.value.phoneNumber);
    return phoneNumber.length === 10;
  }

  confirmCheckin(): void {
    if (!this.smsPreference) return;
    this.router.navigate(['/checkin-success'])
  }

  private getCustomer(): CustomerCheckin {
    return <CustomerCheckin> {
      id: this.customer?.identityId ?? '',
      phoneNumber: this.getOriginalPhoneNumber(this.form.value.phoneNumber),
      firstName: this.form.value.firstName,
      lastName: this.form.value.lastName,
      date: this.storeService.storeGMTDate,
      time: this.storeService.storeGMTTime,
    }
  }

  getOriginalPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) return "";
    if (phoneNumber.includes('+1')) phoneNumber = phoneNumber.replace('+1', '');
    return phoneNumber.replace(/\D/g, '');
  }

  showErrorState(formControlName: string) {
    let control = this.form.controls[formControlName];
    return (control && control.invalid && (control.dirty || control.touched));
  }

  getFullName(): string {
    return `${this.form.value.firstName} ${this.form.value.lastName}`;
  }
}
